import { useMemo } from 'react';
import { Badge } from '@antimatterhq/ui/components/badge';
import { Skeleton } from '@antimatterhq/ui/components/skeleton';
import { DatasourceSummary } from '@/types/api';
import { useGetDatasource } from '@/hooks/core/useGetDataSource';
import { getUpgradePresetVersion, hasUnsavedChanges } from '@/lib/datasource';

const useDatasourceStatus = (
  summaryWithAlertsAndMetrics: DatasourceSummary | undefined,
  isLoadingMetricsAndAlerts: boolean,
) => {
  const { data: datasource, isFetching: isFetchingDatasource } =
    useGetDatasource({
      name: summaryWithAlertsAndMetrics?.name || '',
    });

  const hasUnpublishedChanges = useMemo(() => {
    if (!datasource) {
      return false;
    }
    return hasUnsavedChanges(datasource);
  }, [datasource]);

  const hasUpgradeInProgress = useMemo(() => {
    if (!datasource) {
      return undefined;
    }
    return !!getUpgradePresetVersion(datasource);
  }, [datasource]);

  const hasErrors = useMemo(() => {
    if (!summaryWithAlertsAndMetrics) {
      return false;
    }
    return !!summaryWithAlertsAndMetrics?.alerts?.currentlyOpen;
  }, [summaryWithAlertsAndMetrics]);

  const status = useMemo(() => {
    if (isFetchingDatasource) {
      return 'loading';
    }
    if (hasUpgradeInProgress) {
      return 'upgrading';
    }
    if (hasUnpublishedChanges) {
      return 'unsaved';
    }
    if (isLoadingMetricsAndAlerts) {
      return 'loading';
    }
    if (hasErrors) {
      return 'error';
    }
    return 'healthy';
  }, [
    isFetchingDatasource,
    hasUpgradeInProgress,
    hasUnpublishedChanges,
    isLoadingMetricsAndAlerts,
    hasErrors,
  ]);

  return status;
};

export const DatasourceStatus = ({
  summaryWithAlertsAndMetrics,
  'data-testid': dataTestId,
  isLoadingMetricsAndAlerts,
}: {
  summaryWithAlertsAndMetrics?: DatasourceSummary;
  'data-testid'?: string;
  isLoadingMetricsAndAlerts: boolean;
}) => {
  const status = useDatasourceStatus(
    summaryWithAlertsAndMetrics,
    isLoadingMetricsAndAlerts,
  );

  if (!summaryWithAlertsAndMetrics || status === 'loading') {
    return <Skeleton className="ml-auto h-6 w-16" />;
  }

  return (
    <>
      {status === 'unsaved' && (
        <Badge
          variant="outline"
          color="warning"
          data-testid={`badge-${dataTestId}-unsaved-changes`}
          className="whitespace-nowrap"
        >
          Unsaved changes
        </Badge>
      )}
      {status === 'upgrading' && (
        <Badge
          variant="outline"
          color="warning"
          data-testid={`badge-${dataTestId}-upgrading`}
          className="whitespace-nowrap"
        >
          Upgrade in progress
        </Badge>
      )}
      {status === 'error' && (
        <Badge
          variant="outline"
          color="danger"
          data-testid={`badge-${dataTestId}-error`}
          className="whitespace-nowrap"
        >
          Error
        </Badge>
      )}
      {status === 'healthy' && (
        <Badge
          variant="outline"
          color="success"
          data-testid={`badge-${dataTestId}-healthy`}
          className="whitespace-nowrap"
        >
          Healthy
        </Badge>
      )}
    </>
  );
};
